import {
  CUSTOMER_ORDER_STATUSES,
  type CustomerOrder,
  type CustomerOrderStatus,
} from '@noconflict/contracts';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useDocumentTitle } from '@/shared/lib/use-document-title';
import { NotFoundPage } from '@/widgets/not-found';
import { useOrgOrders } from '../../entities/order';
import { canSeeOrders, projectsHome, useOrgContext } from '../../entities/org';
import { CUSTOMER_STATUS_TONE } from '../../entities/project';
import { useFormat } from '../../shared/i18n/format';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../shared/ui/breadcrumb';
import { Button } from '../../shared/ui/button';
import { Card, CardContent } from '../../shared/ui/card';
import { EmptyState } from '../../shared/ui/empty-state';
import { ErrorState } from '../../shared/ui/error-state';
import { Label } from '../../shared/ui/label';
import { PageLayout } from '../../shared/ui/page-layout';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../shared/ui/select';
import { Skeleton } from '../../shared/ui/skeleton';
import { StatusBadge } from '../../shared/ui/status-badge';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../shared/ui/table';
import { TopBarBreadcrumb } from '../../widgets/app-shell';

const ALL = 'all';
// a draft is never listed here: it is still in its workspace under "Continue your order"
const LISTED_STATUSES = CUSTOMER_ORDER_STATUSES.filter((s) => s !== 'draft');

// Below `sm` the table is a stack: each row a block, each cell a line with its column's name in front.
const STACK_ROW = 'max-sm:block max-sm:py-3';
const STACK_CELL =
  'max-sm:flex max-sm:items-baseline max-sm:justify-between max-sm:gap-4 max-sm:px-0 max-sm:py-1';

/**
 * `/o/:orgSlug/orders`: every order of the organization that the person's grants show (`projects.view` or
 * `orders.pay` on its workspace), colleagues' included, from awaiting payment onward and newest first. The rows
 * and the amounts come from the view `customer_orders`; this page only decides what is offered: a link to the
 * project for someone who can open it, **Pay now** for someone who can pay. An organization whose amounts are
 * hidden gets no amount column. Someone who holds neither permission anywhere gets the not-found page.
 */
export function OrgOrdersPage() {
  const { t } = useTranslation(['order', 'project']);
  useDocumentTitle(t('orders.title'));
  const scope = useOrgContext();
  const { org, canIn, activeWorkspaces, archivedWorkspaces } = scope;
  const allowed = canSeeOrders(scope);
  const { formatCurrency, formatDate, formatDateTime } = useFormat();
  const orders = useOrgOrders(org.id, allowed);
  const [status, setStatus] = useState<string>(ALL);
  const [workspace, setWorkspace] = useState<string>(ALL);

  const workspaces = useMemo(
    () => new Map([...activeWorkspaces, ...archivedWorkspaces].map((w) => [w.id, w])),
    [activeWorkspaces, archivedWorkspaces],
  );
  const rows = useMemo(
    () =>
      (orders.data ?? []).filter(
        (o) =>
          (status === ALL || o.customer_status === status) &&
          (workspace === ALL || o.workspace_id === workspace),
      ),
    [orders.data, status, workspace],
  );
  // amounts are hidden for the whole organization or for none of it
  const showAmounts = !(orders.data ?? []).some((o) => o.amounts_hidden);

  if (!allowed) return <NotFoundPage />;

  /** What the row is for: the report, or for an add-on what was added. */
  const product = (o: CustomerOrder) => {
    const quantity = Number((o.options as { quantity?: unknown }).quantity ?? 0);
    if (o.product === 'script_additional_clearances')
      return { main: t('orders.addonClearances', { count: quantity }), note: t('orders.addon') };
    if (o.product === 'script_versions')
      return { main: t('orders.addonRevisions', { count: quantity }), note: t('orders.addon') };
    return { main: t(`products.${o.product}`), note: null };
  };
  const projectPath = (o: CustomerOrder) => {
    const w = workspaces.get(o.workspace_id);
    return w && canIn(w.id, 'projects.view')
      ? `/o/${org.slug}/w/${w.slug}/projects/${o.project_id}`
      : null;
  };
  const cell = (label: string, children: ReactNode, className = '') => (
    <TableCell className={`${STACK_CELL} ${className}`}>
      <span aria-hidden="true" className="shrink-0 text-sm text-muted-foreground sm:hidden">
        {label}
      </span>
      <span className="min-w-0 max-sm:text-right">{children}</span>
    </TableCell>
  );

  return (
    <PageLayout title={t('orders.title')} description={t('orders.description')}>
      <TopBarBreadcrumb>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={`/o/${org.slug}`}>{org.name}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t('orders.title')}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </TopBarBreadcrumb>

      {orders.isPending ? (
        <section aria-busy="true" aria-label={t('orders.loading')} className="grid gap-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </section>
      ) : null}
      {orders.isError ? (
        <ErrorState
          title={t('orders.error')}
          description={orders.error.message}
          onRetry={() => void orders.refetch()}
        />
      ) : null}
      {orders.isSuccess && orders.data.length === 0 ? (
        <EmptyState
          title={t('orders.emptyTitle')}
          description={t('orders.emptyHelp')}
          action={
            <Button asChild variant="outline">
              <Link to={projectsHome(org, activeWorkspaces)}>{t('orders.goToProjects')}</Link>
            </Button>
          }
        />
      ) : null}
      {orders.isSuccess && orders.data.length > 0 ? (
        <Card>
          <CardContent className="grid gap-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <OrdersFilter
                label={t('orders.filters.status')}
                value={status}
                onChange={setStatus}
                allLabel={t('orders.filters.all')}
                options={LISTED_STATUSES.map((s) => [s, t(`project:status.${s}`)])}
              />
              {workspaces.size > 1 ? (
                <OrdersFilter
                  label={t('orders.filters.workspace')}
                  value={workspace}
                  onChange={setWorkspace}
                  allLabel={t('orders.filters.all')}
                  options={[...workspaces.values()].map((w) => [w.id, w.name])}
                />
              ) : null}
            </div>
            {rows.length === 0 ? (
              <EmptyState size="compact" title={t('orders.noMatch')} />
            ) : (
              <Table size="compact" className="max-sm:block">
                <TableCaption className="sr-only">{t('orders.title')}</TableCaption>
                <TableHeader className="max-sm:sr-only">
                  <TableRow>
                    <TableHead scope="col">{t('orders.columns.product')}</TableHead>
                    <TableHead scope="col">{t('orders.columns.title')}</TableHead>
                    <TableHead scope="col">{t('orders.columns.workspace')}</TableHead>
                    <TableHead scope="col">{t('orders.columns.ordered')}</TableHead>
                    <TableHead scope="col">{t('orders.columns.due')}</TableHead>
                    <TableHead scope="col">{t('orders.columns.status')}</TableHead>
                    {showAmounts ? (
                      <TableHead scope="col" className="text-right">
                        {t('orders.columns.amount')}
                      </TableHead>
                    ) : null}
                  </TableRow>
                </TableHeader>
                <TableBody className="max-sm:block">
                  {rows.map((o) => {
                    const what = product(o);
                    const to = projectPath(o);
                    const customerStatus: CustomerOrderStatus = o.customer_status;
                    const mayPay =
                      o.status === 'awaiting_payment' && canIn(o.workspace_id, 'orders.pay');
                    return (
                      <TableRow key={o.id} className={STACK_ROW}>
                        {cell(
                          t('orders.columns.product'),
                          <>
                            <span className="block font-bold text-heading">{what.main}</span>
                            {what.note ? (
                              <span className="block text-sm text-muted-foreground">
                                {what.note}
                              </span>
                            ) : null}
                          </>,
                        )}
                        {cell(
                          t('orders.columns.title'),
                          to ? (
                            <Link className="break-words text-link hover:text-link-hover" to={to}>
                              {o.project_title}
                            </Link>
                          ) : (
                            <span className="break-words">{o.project_title}</span>
                          ),
                          'sm:min-w-40',
                        )}
                        {cell(
                          t('orders.columns.workspace'),
                          workspaces.get(o.workspace_id)?.name ?? t('orders.none'),
                        )}
                        {cell(
                          t('orders.columns.ordered'),
                          <>
                            <span className="block">{o.ordered_by_name ?? t('orders.none')}</span>
                            {o.submitted_at ? (
                              <span className="block whitespace-nowrap text-muted-foreground">
                                {formatDate(o.submitted_at)}
                              </span>
                            ) : null}
                          </>,
                        )}
                        {cell(
                          t('orders.columns.due'),
                          o.due_at ? formatDateTime(o.due_at) : t('orders.none'),
                        )}
                        {cell(
                          t('orders.columns.status'),
                          <span className="grid justify-items-start gap-2 max-sm:justify-items-end">
                            <StatusBadge size="compact" tone={CUSTOMER_STATUS_TONE[customerStatus]}>
                              {t(`project:status.${customerStatus}`)}
                            </StatusBadge>
                            {mayPay ? (
                              <Button asChild size="sm">
                                <Link
                                  to={`/o/${org.slug}/orders/${o.id}/pay`}
                                  aria-label={t('orders.payNowNamed', { name: o.project_title })}
                                >
                                  {t('orders.payNow')}
                                </Link>
                              </Button>
                            ) : null}
                          </span>,
                        )}
                        {showAmounts
                          ? cell(
                              t('orders.columns.amount'),
                              o.amount_cents !== null && o.currency
                                ? formatCurrency(o.amount_cents, o.currency)
                                : t('orders.none'),
                              'sm:text-right sm:whitespace-nowrap',
                            )
                          : null}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      ) : null}
    </PageLayout>
  );
}

/** A labelled select above the list: "All" or one of the options. */
function OrdersFilter({
  label,
  value,
  onChange,
  options,
  allLabel,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly (readonly [string, string])[];
  allLabel: string;
}) {
  return (
    <div className="grid gap-1">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger aria-label={label}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{allLabel}</SelectItem>
          {options.map(([id, name]) => (
            <SelectItem key={id} value={id}>
              {name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
